import { useLocation, useParams } from 'react-router-dom';
import { Breadcrumbs } from '../../../../core/components/organism/Breadcrumbs';
import { useDropdownHook } from '../../hooks/useDropdownHook';
import { Typography } from '../../../../core/components/molecules/Typography';
import { useCallback, useEffect, useRef } from 'react';
import { Colors } from '../../../../core/models/disciplines';
import { useSubject } from '../../hooks/useSubject';
import { useModule } from '../../hooks/useModule';
import { useNewModuleStore } from '../../stores/newModuleStore';
import { getDisciplineName } from '../../../../core/utils/discipline';
import { useNewSubjectStore } from '../../stores/newSubjectStore';
import { useExerciseStore } from '../../stores/exercisesStore';
import {
  getModuleDescription,
  getModuleLabel,
} from '../../utils/contentHelper';
import Container from '../../../../core/components/atoms/Container';
import PageHeader from '../../../../core/components/molecules/Header';
import BrainLoadingWrapper from '../../../../core/components/molecules/BrainLoading';
import ContentButtonGroup from '../../components/ContentButtonGroup';
import SubjectSelectSection from '../../components/SubjectsSelect';
import LessonList from '../../components/lessons';
import MaterialList from '../../components/materials';
import PodcastList from '../../components/podcasts';
import ExerciseList from '../../components/exercises';
import { useFilterConfigStore } from '../../stores/filterConfig';
import { useNewExerciseHook } from '../../hooks/useNewExercise';
import { applyExerciseFiltersFromUrl, useExerciseUrlFilters } from '../../hooks/useExerciseUrlFilters';
import { useLogDossieAccess } from '../../hooks/useLogDossieAccess';

export default function ContentPage() {
  const { module, discipline, type } = useParams();
  const { pathname } = useLocation();
  const prevPathname = useRef(pathname);

  // Registra o acesso ao dossiê de incidências e à aba aberta. Inerte nos demais
  // módulos.
  useLogDossieAccess({ discipline, module, type });

  const { mutate, isPending } = useSubject();
  const { mutate: getModules, isPending: loadingModules } = useModule();
  const { mutate: refetchExercises } = useNewExerciseHook();

  const { modules } = useNewModuleStore();
  const { subjects, setSelectedSubjects } = useNewSubjectStore();
  const { resetExercises, setPage, setIsLoading } = useExerciseStore();
  const { writeStoresToUrl } = useExerciseUrlFilters();
  const hasHydratedRef = useRef<string>('');

  // Hidrata de forma síncrona durante o render: o primeiro fetch do
  // ExerciseList (efeito de mount do filho) dispara ANTES de qualquer effect do
  // pai, então um useEffect aqui chegaria tarde demais e a lista viria sem os
  // filtros da URL.
  const hydrationKey = `${module}:${type}`;
  if (type === 'exercicio' && hasHydratedRef.current !== hydrationKey) {
    hasHydratedRef.current = hydrationKey;
    applyExerciseFiltersFromUrl();
  }

  const { array: dropdownItems } = useDropdownHook(
    modules || [],
    pathname,
    module || ''
  );

  useEffect(() => {
    if (discipline) {
      getModules(discipline);
    }
  }, [discipline]);

  useEffect(() => {
    resetExercises();
  }, [discipline, module]);

  useEffect(() => {
    if (module) {
      const actualModule = modules.find((m) => m.slug === module);

      if (
        actualModule &&
        subjects &&
        subjects[0] &&
        subjects[0].subarea !== actualModule?.id
      ) {
        setSelectedSubjects([]);
      }

      mutate(module);
    }
  }, [module]);

  useEffect(() => {
    if (pathname !== prevPathname.current) {
      const isCurrentPathExercise = pathname.endsWith('/exercicio');
      const isPrevPathExercise = prevPathname.current.endsWith('/exercicio');

      if (isCurrentPathExercise || isPrevPathExercise) {
        useExerciseStore.getState().clearFilters();
        useFilterConfigStore.getState().resetFilters();
      }

      prevPathname.current = pathname;
    }
  }, [pathname]);

  const contentUrl = `/disciplinas/${discipline}/modulo/${module}`;

  const handleFilterChange = useCallback(() => {
    if (module && type === 'exercicio') {
      setIsLoading(true);
      setPage(1);
      writeStoresToUrl();
      refetchExercises({ module, operation: 'initial', page: 1 });
    }
  }, [module, type, refetchExercises, setPage, setIsLoading, writeStoresToUrl]);

  const genericLabel = () => {
    if (discipline) {
      return getDisciplineName(discipline);
    }

    return '-';
  };

  const showContent = () => {
    if (type === 'exercicio') {
      return (
        <ExerciseList
          module={module || ''}
          discipline={discipline as Colors}
          url={contentUrl}
        />
      );
    }

    if (type === 'materiais') {
      return (
        <MaterialList
          module={module || ''}
          discipline={discipline as Colors}
          url={contentUrl}
        />
      );
    }

    if (type === 'podcast') {
      return (
        <PodcastList module={module || ''} discipline={discipline as Colors} />
      );
    }

    return (
      <LessonList
        module={module || ''}
        discipline={discipline as Colors}
        url={contentUrl}
      />
    );
  };

  return (
    <Container direction='col' gap='xxl'>
      <Breadcrumbs
        items={[
          {
            label: genericLabel(),
            href: `/disciplinas/${discipline}`,
          },
          {
            label: getModuleLabel(module || ''),
            href: '#',
          },
        ]}
      />
      <PageHeader
        module='Módulo'
        title={getModuleLabel(module || '')}
        isDropdown
        dropdownItems={dropdownItems}
        className='text-left'
        loading={(!modules || modules?.length === 0) && loadingModules}
        isDiscipline
        shouldShowHowToScoreCard={true}
      >
        <Typography>{getModuleDescription(module || '')}</Typography>
      </PageHeader>
      <ContentButtonGroup type={type ?? ''} contentUrl={contentUrl} />
      {isPending ? (
        <BrainLoadingWrapper />
      ) : (
        <>
          <SubjectSelectSection type={type ?? ''} onFilterChange={handleFilterChange} />
          {showContent()}
        </>
      )}
    </Container>
  );
}
